import { Button, HStack, Td, Text, Tr, VStack } from '@chakra-ui/react';
import { ChevronDownIcon, Icon } from '@chakra-ui/icons';
import { format, parseISO, isPast } from 'date-fns';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HTTP } from 'utils/http';
import { resolveUserError } from 'utils/userErrorMessages';
import { useToast } from '@chakra-ui/react';
import { CancelWaitlistModal } from './CancelWaitlistModal';

// Type pour les entrées de waitlist
interface WaitlistEntry {
  Id: number;
  ClassId: number;
  ClassDate: string;
  StartTime: string;
  StartDateTime: string;
  SiteId?: number;
  ClassSchedule?: {
    StartTime: string;
    ClassDescription?: {
      Name?: string;
    };
    Staff?: {
      Id: number;
    };
    Location?: {
      Id: number;
    };
  };
}

interface WaitlistHistoryItemProps {
  index: number;
  entry: WaitlistEntry;
  coachName?: string;
  locationName?: string;
  isMobile: boolean;
}

const WaitlistHistoryItem: React.FC<WaitlistHistoryItemProps> = ({
  index,
  entry,
  coachName,
  locationName,
  isMobile,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();

  // Créer une date combinée à partir de ClassDate et ClassSchedule.StartTime
  const getDateTime = () => {
    if (!entry.ClassDate || !entry.ClassSchedule?.StartTime) {
      return new Date(); // Retourne la date actuelle si les données sont manquantes
    }
    const datePart = entry.ClassDate.split('T')[0]; // "2025-04-06"
    const timePart = entry.ClassSchedule.StartTime.split('T')[1]; // "17:00:00"
    return new Date(`${datePart}T${timePart}`);
  };

  const isInPast = isPast(getDateTime());

  if (isInPast) {
    return null;
  }

  const handleToggleDetails = () => setShowDetails(!showDetails);

  const rotateIcon = showDetails ? 'rotate(180deg)' : 'rotate(0)';

  const handleCancelSuccess = () => {
    setIsCancelled(true);
  };

  // Fonction pour formater la date de manière sécurisée
  const formatDate = (classDate: string | undefined, startTime: string | undefined) => {
    if (!classDate || !startTime) {
      return 'Date non disponible';
    }

    try {
      // Extraire la date du ClassDate
      const date = parseISO(classDate);

      // Extraire l'heure du StartTime (qui est au format "1899-12-30T10:00:00")
      const time = startTime.split('T')[1].slice(0, 5); // Récupère "10:00"

      // Formater la date
      const formattedDate = format(date, 'dd/MM/yyyy');

      return `${formattedDate} - ${time}`;
    } catch (error) {
      return 'Date invalide';
    }
  };

  const handleCancelWaitlist = async () => {
    try {
      await HTTP.delete(`/class/${entry.ClassId}/waitlist`).send();
      queryClient.setQueryData(['/class', entry.ClassId, '/waitlist'], null);
      queryClient.invalidateQueries({ queryKey: ['waitlist'] });
      handleCancelSuccess();
      toast({
        title: "Vous avez quitté la liste d'attente",
        status: 'success',
      });
    } catch (error) {
      const { title, description } = resolveUserError('cancelWaitlist', error);
      toast({ title, description, status: 'error', isClosable: true });
    }
  };

  const cancelButton = (
    <CancelWaitlistModal entry={entry} onCancelSuccess={handleCancelWaitlist}>
      <Button
        textTransform="uppercase"
        sx={{ background: 'transparent', marginTop: 4, color: 'black', textDecoration: 'underline' }}
        size="sm"
      >
        Annuler la liste d'attente
      </Button>
    </CancelWaitlistModal>
  );

  return (
    <>
      <Tr key={`waitlist_${entry.Id}_${index}`}>
        <Td width={isMobile ? '33%' : 'auto'} padding={isMobile ? '5px' : 'initial'}>
          <Text textTransform={'uppercase'} fontWeight="semibold">
            {entry.ClassSchedule?.ClassDescription?.Name || 'Cours'}
          </Text>
        </Td>
        <Td width={isMobile ? '33%' : 'auto'} padding={isMobile ? '5px' : 'initial'}>
          <HStack justifyContent={'space-between'}>
            <Text align="left">{formatDate(entry.ClassDate, entry.ClassSchedule?.StartTime)}</Text>
          </HStack>
        </Td>
        <Td
          width={isMobile ? '100%' : 'auto'}
          padding={isMobile ? '5px' : 'initial'}
          display={isMobile ? 'flex' : 'table-cell'}
        >
          <Text align="left">{locationName ?? '-'}</Text>
          {isMobile && (
            <Button size="md" onClick={handleToggleDetails} variant="ghost">
              <Icon as={ChevronDownIcon} transition="transform .2s ease-in-out" transform={rotateIcon} />
            </Button>
          )}
        </Td>
        {!isMobile && (
          <>
            <Td>
              <Text align="left">{coachName ?? '-'}</Text>
            </Td>
            <Td>
              {isCancelled ? (
                <Text align="center" fontWeight="700" color="red.500" textTransform="uppercase" mt={4} fontSize="sm">
                  Annulée
                </Text>
              ) : (
                cancelButton
              )}
            </Td>
          </>
        )}
      </Tr>
      {isMobile && showDetails && (
        <Tr>
          <Td colSpan={6}>
            <VStack align="start" spacing={2}>
              <Text>
                <strong>Coach :</strong> {coachName ?? '-'}
              </Text>
              <Text>
                <strong>Action :</strong>
                {isCancelled ? (
                  <Text align="center" fontWeight="700" color="red.500" textTransform="uppercase" mt={4} fontSize="sm">
                    Annulée
                  </Text>
                ) : (
                  cancelButton
                )}
              </Text>
            </VStack>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default WaitlistHistoryItem;
